/**
 * CompanyLogo — small square logo for a company. Priority chain:
 *
 *   1. `logoUrl`  — explicit URL (typically a LinkedIn-CDN logo we've
 *                   scraped via BrightData / Coresignal). Most reliable
 *                   because it's the company's own uploaded logo.
 *   2. logo.dev   — when we have a `domain` or `websiteUrl` and the
 *                   `VITE_LOGO_DEV_TOKEN` env var is set. Algorithmically
 *                   sourced; quality varies by company.
 *   3. Initials   — styled fallback square when the firm has no logo on
 *                   file and no domain we can hand to logo.dev.
 *
 * Free-tier attribution for logo.dev: page footer (handled once
 * globally in AppLayout, not here).
 *
 * Usage:
 *   <CompanyLogo logoUrl={p.company.logo_url} domain={p.company.domain} name={p.company.name} />
 *   <CompanyLogo websiteUrl={lr.company_website} name={lr.company_name ?? '?'} />
 *   <CompanyLogo name="Some Private Co" />   // → initials only
 */

import { useState } from 'react';
import styles from './CompanyLogo.module.css';

export interface CompanyLogoProps {
  /** Explicit logo URL — preferred over logo.dev when supplied. */
  logoUrl?: string | null;
  /** Bare web domain — fallback when no `logoUrl`. */
  domain?: string | null;
  /**
   * Full website URL (e.g. `https://www.expedition.capital`). Used as a
   * fallback when only a website URL is available (LinkedIn-only roles).
   * Ignored if `domain` or `logoUrl` is also set.
   */
  websiteUrl?: string | null;
  /** Used for initials-fallback + alt text. */
  name: string;
  /** Pixel size of the rendered logo. Default 32 (timeline-row sized). */
  size?: number;
  /** Apply a rounded-square shape (default true). */
  rounded?: boolean;
  className?: string;
}

const LOGO_DEV_TOKEN = import.meta.env.VITE_LOGO_DEV_TOKEN as string | undefined;

/**
 * Strip protocol + leading `www.` to get the bare domain. Strips path,
 * query, fragment. Returns null if the input doesn't parse to anything
 * useful.
 */
export function domainFromUrl(raw?: string | null): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw.trim());
    let host = u.hostname.toLowerCase();
    if (host.startsWith('www.')) host = host.slice(4);
    return host || null;
  } catch {
    // Not a valid URL — try to salvage if it's already a bare domain.
    const stripped = raw.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
    const host = stripped.split('/')[0].split('?')[0];
    return host && host.includes('.') ? host : null;
  }
}

function initialsOf(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return '?';
  // Strip common legal-form suffixes for cleaner initials.
  const tokens = cleaned
    .split(/\s+/)
    .filter((t) => !/^(ltd|limited|llc|llp|plc|inc|corp|co|sa|nv|ag|gmbh)\.?$/i.test(t));
  if (tokens.length === 0) return cleaned.charAt(0).toUpperCase();
  if (tokens.length === 1) return tokens[0].charAt(0).toUpperCase();
  return (tokens[0].charAt(0) + tokens[tokens.length - 1].charAt(0)).toUpperCase();
}

export function CompanyLogo({
  logoUrl,
  domain,
  websiteUrl,
  name,
  size = 32,
  rounded = true,
  className,
}: CompanyLogoProps) {
  // If the chosen `<img>` URL fails to load, fall through to the next
  // tier in the priority chain. Two booleans track which sources we've
  // already tried and given up on.
  const [logoUrlFailed, setLogoUrlFailed] = useState(false);
  const [logoDevFailed, setLogoDevFailed] = useState(false);

  const dim = { width: size, height: size };
  const cls = [
    styles.logo,
    rounded ? styles.rounded : styles.square,
    className,
  ].filter(Boolean).join(' ');

  // Tier 1 — explicit logo URL (LinkedIn-CDN, etc.).
  if (logoUrl && !logoUrlFailed) {
    return (
      <img
        className={cls}
        src={logoUrl}
        alt={`${name} logo`}
        style={dim}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setLogoUrlFailed(true)}
      />
    );
  }

  // Tier 2 — logo.dev via domain or website URL.
  const resolvedDomain = domain ?? domainFromUrl(websiteUrl);
  if (resolvedDomain && LOGO_DEV_TOKEN && !logoDevFailed) {
    // Retina: request 2× pixels and downscale visually for crisp HiDPI.
    const requestedSize = size * 2;
    const url =
      `https://img.logo.dev/${encodeURIComponent(resolvedDomain)}` +
      `?token=${LOGO_DEV_TOKEN}` +
      `&size=${requestedSize}` +
      `&format=webp` +
      `&retina=true` +
      `&fallback=monogram`;
    return (
      <img
        className={cls}
        src={url}
        alt={`${name} logo`}
        style={dim}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setLogoDevFailed(true)}
      />
    );
  }

  // Tier 3 — styled initials fallback.
  return (
    <div
      className={`${cls} ${styles.fallback}`}
      style={{ ...dim, fontSize: Math.max(10, Math.round(size * 0.4)) }}
      aria-hidden
    >
      {initialsOf(name)}
    </div>
  );
}
